import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { receivableReconciliationForManager } from '@/apis/aipass'
import { AccountDescriptionType } from '@/models/accounts-receivable-manager'
import type { InputReceipts } from '@/models/accounts-receivable-manager'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { LoaderContextCreator } from '@/contexts/loader'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import dayjs from 'dayjs'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import uuid from 'react-uuid'
import { DateField } from '@/components/molecules/date-field'
import { InputField } from '@/components/molecules/input-field'

type FormValueType = {
  receipts: InputReceipts[]
}

type Props = {
  targetSaleIds: string[]
  targetDiscountIds: string[]
  totalPrice: number
  onCancel: () => void
  onSave: () => void
}

const defaultFieldValue: InputReceipts = {
  accountDescription: AccountDescriptionType.AccountsReceivable,
  receiptPrice: 0,
  receiptDate: dayjs().format('YYYY-MM-DD'),
}

export const ReceiptReconciliationModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const [triggerRemoveField, setTriggerRemoveField] = useState(false)
  const { control, handleSubmit } = useForm<FormValueType>({
    defaultValues: {
      receipts: [defaultFieldValue],
    },
  })
  const receiptInputArray = useFieldArray({
    control,
    name: 'receipts',
    rules: {
      validate: () => calcTotalInputReceipt === calcTotalARPrice,
    },
  })
  const watchReceipts = useWatch({ control, name: 'receipts' })

  const onSave = async (value: FormValueType) => {
    if (!isValid) {
      return
    }
    try {
      setIsLoading(true)
      await receivableReconciliationForManager({
        saleIds: props.targetSaleIds,
        discountIds: props.targetDiscountIds,
        receipts: value.receipts,
      })
      props.onSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const calcTotalARPrice = useMemo((): number => props.totalPrice, [props.totalPrice])

  const calcTotalInputReceipt = useMemo((): number => {
    return watchReceipts.reduce((prev, current) => prev + Number(current.receiptPrice), 0)
  }, [watchReceipts])

  const isValid = useMemo((): boolean => calcTotalInputReceipt === calcTotalARPrice, [calcTotalARPrice, calcTotalInputReceipt])

  return (
    <Modal customCss={modalStyle}>
      <ModalHeader>
        {t('Receipt reconciliation process')}
        <div className="sub-title">
          {t('Processes the cancellation of the selected accounts receivable. Enter the deposit information.')}
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="total-price-block">
          <div>{t('Total A/R')}</div>
          <div>¥{calcTotalARPrice.toLocaleString()}</div>
        </div>
        <div>
          {receiptInputArray.fields.map((input, index) => (
            <div className="receipt-form-field" key={input.id}>
              <div className="form-title">
                <div>{`${t('Deposit information')}${index + 1}`}</div>
                {receiptInputArray.fields.length > 1 && (
                  <img
                    src={require('@/static/images/icon_delete.svg')}
                    onClick={() => {
                      receiptInputArray.remove(index)
                      setTriggerRemoveField(!triggerRemoveField)
                    }}
                  />
                )}
              </div>
              <Controller
                control={control}
                name={`receipts.${index}.accountDescription`}
                rules={{ required: t('Required field has not been entered') }}
                render={({ field: { onChange, value } }) => (
                  <div className="input-field">
                    <div className="input-title">{t('Summary')}</div>
                    <Select customStyle={css({ width: 150, paddingBottom: 0 })} value={value} onChange={onChange}>
                      {Object.keys(AccountDescriptionType).map(d => (
                        <AntSelect.Option key={uuid()} value={AccountDescriptionType[d]}>
                          {AccountDescriptionType[d]}
                        </AntSelect.Option>
                      ))}
                    </Select>
                  </div>
                )}
              />
              <Controller
                control={control}
                name={`receipts.${index}.receiptPrice`}
                rules={{
                  required: t('Required field has not been entered'),
                  pattern: { value: /^\d+$/, message: t('Please enter numbers only, without symbols or spaces') },
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className="input-field">
                    <div className="input-title">{t('Receipt price')}</div>
                    <InputField type="number" value={value} onChange={onChange} error={error?.message} />
                  </div>
                )}
              />
              <Controller
                control={control}
                name={`receipts.${index}.receiptDate`}
                rules={{ required: t('Required field has not been entered') }}
                render={({ field: { onChange, value } }) => (
                  <div className="input-field">
                    <div className="input-title">{t('Deposit day')}</div>
                    <DateField
                      value={value}
                      onChange={m => onChange(m?.format('YYYY-MM-DD'))}
                      customCss={css({
                        'div.SingleDatePicker': {
                          '.SingleDatePickerInput': {
                            'div.SingleDatePicker_picker': {
                              'div.DayPicker': { left: 0, top: -190 },
                            },
                          },
                        },
                      })}
                    />
                  </div>
                )}
              />
            </div>
          ))}
        </div>
        <div className="add-button-block">
          <AddFormFieldButton
            label={t('Add deposit information')}
            onAdd={() => {
              receiptInputArray.append(defaultFieldValue)
            }}
          />
        </div>
        <div style={{ paddingTop: 16 }}>
          <div className="total-price-block">
            <div>{t('Receipt total')}:</div>
            <div>¥{calcTotalInputReceipt.toLocaleString()}</div>
          </div>
          {calcTotalInputReceipt !== calcTotalARPrice && (
            <div className="error-text">{t('Total Receivables does not equal Total Accounts Receivable.')}</div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button buttonType={ButtonType.Secondary} height="38px" width="110px" marginRight={16} onClick={props.onCancel}>
          {t('Cancel')}
        </Button>
        <Button
          buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
          height="38px"
          width="110px"
          onClick={handleSubmit(onSave)}
        >
          {t('Confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css({
  height: 586,
  minHeight: 586,
  maxHeight: 586,
  width: 662,
  minWidth: 662,
  left: 'calc((100% - 662px) / 2)',
  h3: {
    '.sub-title': {
      color: '#676767',
      fontSize: 14,
      paddingTop: 8,
      fontWeight: 'normal',
    },
  },
  '.receipt-form-field': {
    marginTop: 16,
    padding: 16,
    background: 'white',
    color: 'rgb(103, 103, 103)',
    border: '1px solid rgb(217, 217, 217)',
    borderRadius: 5,
    '.form-title': {
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: 16,
      img: {
        padding: 8,
        borderRadius: 50,
        cursor: 'pointer',
        ':hover': { background: '#F2F2F2' },
      },
    },
    '.input-field': {
      marginBottom: 16,
      '.input-title': {
        display: 'flex',
        fontSize: 12,
        fontWeight: 'bold',
        letterSpacing: 0.6,
        paddingBottom: 12,
      },
    },
  },
  '.add-button-block': {},
  '.total-price-block': {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 'bold',
  },
  '.error-text': { paddingTop: 16, fontSize: 12, fontWeight: 'bold', color: 'rgba(255,0,0,0.5)' },
})
